import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Input,
  Table,
  Button,
  message,
  Form,
  Row,
  Col,
  Modal,
  Card,
  Tooltip,
  Popconfirm,
  Tag,
  Select,
  Alert,
} from "antd";
import { CodeOutlined, EyeOutlined, PlusOutlined, DeleteOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { showAuthLoader, hideAuthLoader } from "../../appRedux/actions/Auth";
import Service from "../../service";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import './DocumentMaster.css';
import EditIcon from "../../assets/images/EditIcon";
import { capitalizeFirst } from "../../util/YearOptions";
import NodataFoundIcon from "../../assets/images/NodataFoundIcon";
import DeleteIcon from "../../assets/images/DeleteIcon";
import {
  ALLOWED_SYSTEM_VARIABLES,
  COMPANY_DOC_MASTER_VARIABLES,
  DOC_FOR_LABELS,
} from "../../constants/documentTemplateVariables";
import { DEFAULT_PAGE_SIZE, TABLE_PAGINATION } from "../../constants/tablePagination";

const { TextArea } = Input;

const VALUE_TYPES = [
  { label: "Text", value: "text" },
  { label: "Number", value: "number" },
];

/** Every employee-completed, manager-approved form. They share a palette. */
const REVIEW_DOC_FORS = ["wil_form", "review_3_month", "review_6_month", "review_1_year"];

/** The new-joiner pack. Raised together at employee creation, so one palette. */
const JOINING_DOC_FORS = [
  "appointment_letter",
  "id_card",
  "personal_data_form",
  "pf_registration_sheet",
];

/** The one pack document exported to a spreadsheet rather than a Word file. */
const PF_REGISTRATION_DOC_FOR = "pf_registration_sheet";

/**
 * Targeting dimensions. An EMPTY selection means "applies to all" - the one
 * rule an admin must not have to guess at, which is why each placeholder says
 * so outright rather than relying on a tooltip.
 */
const TARGETING_FIELDS = [
  { name: "emp_types", label: "Employment Type", placeholder: "Leave empty for all employment types" },
  { name: "designations", label: "Designation", placeholder: "Leave empty for all designations" },
  { name: "departments", label: "Department", placeholder: "Leave empty for all departments" },
];

const SEPARATION_DOC_FORS = ["experience_letter", "relieving_letter", "non_due_letter"];
const DISCONTINUATION_DOC_FOR = "discontinuation_form";
const PROMOTION_DOC_FOR = "promotion_letter";
const APPRAISAL_DOC_FOR = "appraisal_letter";

/** Every document raised when someone leaves - they share a signatory block. */
const EXIT_DOC_FORS = [...SEPARATION_DOC_FORS, DISCONTINUATION_DOC_FOR];

/**
 * Formal letters signed by the company. They draw nothing from Company Document
 * Master beyond the signatory block, so offering the onboarding-era photo and
 * email fields here would only be noise.
 */
const SIGNATORY_ONLY_DOC_FORS = [...EXIT_DOC_FORS, PROMOTION_DOC_FOR];

// Everything an onboarding document can use. Onboarding letters draw on the
// whole employee record - offer terms, bank details, addresses - so this list
// stays exhaustive.
const ONBOARDING_VARIABLE_GROUPS = [
  {
    title: "Basic Information",
    variables: [
      { label: "Name", key: "name" },
      { label: "First Name", key: "firstName" },
      { label: "Last Name", key: "lastName" },
      { label: "Initial", key: "initialName" },
      { label: "Email", key: "email" },
      { label: "Contact Number", key: "contactNumber" },
      { label: "Employee Full Name", key: "employee_full_name" },
      { label: "Employee First Name", key: "employee_first_name" },
    ],
  },
  {
    title: "Personal Details",
    variables: [
      { label: "Gender", key: "gender" },
      { label: "Birthdate", key: "birthdate" },
      { label: "Marital Status", key: "maritalStatus" },
      { label: "Father Name", key: "fatherName" },
      { label: "Mother Name", key: "motherName" },
    ],
  },
  {
    title: "Permanent Address",
    variables: [
      { label: "Address", key: "address" },
      { label: "City", key: "city" },
      { label: "State", key: "state" },
      { label: "District", key: "district" },
      { label: "Country", key: "country" },
      { label: "PIN Code", key: "pinCode" },
    ],
  },
  {
    title: "Present Address",
    variables: [
      { label: "Address", key: "address2" },
      { label: "City", key: "city2" },
      { label: "State", key: "state2" },
      { label: "District", key: "district2" },
      { label: "Country", key: "country2" },
      { label: "PIN Code", key: "pinCode2" },
    ],
  },
  {
    title: "Employment Details",
    variables: [
      { label: "Date of Joining", key: "doj" },
      { label: "Joining Date", key: "joiningDate" },
      { label: "Date of Joining", key: "date_of_joining" },
      { label: "Experience", key: "experience" },
      { label: "Offered Salary", key: "offeredSalary" },
      { label: "Monthly Salary", key: "monthly_salary" },
      { label: "Annual CTC", key: "annualCTC" },
      { label: "Annual CTC", key: "annual_ctc" },
      { label: "Stipend Amount", key: "stipendAmount" },
    ],
  },
  {
    title: "Designation & Reporting Manager",
    variables: [
      { label: "Designation", key: "designation_name" },
      { label: "Reporting Manager Name", key: "reporting_manager_name" },
      { label: "Reporting Manager Designation", key: "reporting_manager_designation" },
    ],
  },
  {
    title: "Bank Details",
    variables: [
      { label: "Bank Name", key: "bankName" },
      { label: "Account Holder Name", key: "accountHolderName" },
      { label: "Account Number", key: "accountNumber" },
      { label: "IFSC Code", key: "ifsc" },
      { label: "Bank Branch", key: "bankBranch" },
    ],
  },
  {
    title: "Cheque Details",
    variables: [
      { label: "Cheque Number", key: "chequeNumber" },
      { label: "Cheque Amount", key: "chequeAmount" },
    ],
  },
  {
    title: "Company Details",
    variables: [
      { label: "Company Name", key: "companyName" },
      { label: "Company Name", key: "company_name" },
      { label: "HR Name", key: "hr_name" },
    ],
  },
  {
    title: "Dates",
    variables: [
      { label: "Current Date", key: "date" },
      { label: "Current Date", key: "currentDate" },
      { label: "Current Date", key: "current_date" },
      { label: "Today", key: "today" },
      { label: "Document Generate Date", key: "documentGenerateDate" },
      { label: "Employment End Date", key: "employmentEndDate" },
      { label: "Offer Letter Return Date", key: "olReturnDate" },
      { label: "Offer Acceptance Date", key: "offer_acceptance_date" },
    ],
  },
  {
    title: "Other",
    variables: [
      { label: "Qualifications", key: "qualifications" },
      { label: "Hobbies", key: "hobbies" },
      { label: "Probation Period", key: "probation_period" },
    ],
  },
];

// Experience and relieving letters state a handful of facts: who left, what
// they did, how long they were here, and when they stopped. Listing the whole
// employee record here only invites templates full of fields nobody signs off
// on. Anything omitted still works if typed by hand - this list is the
// recommended set, not a whitelist.
const SEPARATION_VARIABLE_GROUPS = [
  {
    title: "Employee",
    variables: [
      { label: "Name", key: "name" },
      { label: "Employee Full Name", key: "employee_full_name" },
      { label: "First Name", key: "firstName" },
      { label: "Last Name", key: "lastName" },
      { label: "Initial (Mr./Ms.)", key: "initialName" },
      { label: "Gender", key: "gender" },
      { label: "Employee Address", key: "employee_address" },
    ],
  },
  {
    title: "Role & Tenure",
    variables: [
      { label: "Designation", key: "designation_name" },
      { label: "Date of Joining", key: "date_of_joining" },
      { label: "Date of Joining (alias)", key: "doj" },
      { label: "Date of Joining (alias)", key: "joiningDate" },
      { label: "Experience", key: "experience" },
    ],
  },
  {
    title: "Separation Details",
    variables: [
      { label: "Last Working Date", key: "last_date" },
      { label: "Last Working Date (alias)", key: "last_working_date" },
      { label: "Last Working Date (alias)", key: "emp_last_working_date" },
      { label: "Relieving Date", key: "relieving_date" },
      { label: "Resignation Date", key: "resignation_date" },
    ],
  },
  {
    title: "Company",
    variables: [
      { label: "Company Name", key: "companyName" },
      { label: "Company Name (alias)", key: "company_name" },
      { label: "Company Address", key: "address" },
      { label: "Place", key: "place" },
    ],
  },
  {
    title: "Letter Date",
    variables: [
      { label: "Current Date", key: "date" },
      { label: "Current Date (alias)", key: "currentDate" },
      { label: "Today", key: "today" },
    ],
  },
];

// The discontinuation form records why an employment ended, so on top of who
// the person was it carries every field the Add Resignation form captures.
const DISCONTINUATION_VARIABLE_GROUPS = [
  {
    title: "Employee",
    variables: [
      { label: "Name", key: "name" },
      { label: "Employee Full Name", key: "employee_full_name" },
      { label: "First Name", key: "firstName" },
      { label: "Last Name", key: "lastName" },
      { label: "Initial (Mr./Ms.)", key: "initialName" },
      { label: "Employee Code", key: "emp_code" },
      { label: "Employee Address", key: "employee_address" },
    ],
  },
  {
    title: "Role & Placement",
    variables: [
      { label: "Designation", key: "designation_name" },
      { label: "Department", key: "department_name" },
      { label: "Work Location", key: "work_location" },
      { label: "Date of Joining", key: "date_of_joining" },
      { label: "Reporting Manager", key: "reporting_manager_name" },
    ],
  },
  {
    title: "Discontinuation Details",
    variables: [
      { label: "Reason Type (Retirement / Terminated / Death / Absconded)", key: "reason_type" },
      { label: "Reason", key: "reason" },
      { label: "Other Reason", key: "other_reason" },
      { label: "Notice Received On", key: "resignation_date" },
      { label: "Accepted / Approved On", key: "resignation_accepted_date" },
      { label: "Last Working Date", key: "last_working_date" },
      { label: "Last Working Date (alias)", key: "last_date" },
      { label: "Notice Period", key: "notice_period" },
      { label: "Exit Interview", key: "exit_interview" },
    ],
  },
  {
    title: "Company",
    variables: [
      { label: "Company Name", key: "companyName" },
      { label: "Company Name (alias)", key: "company_name" },
      { label: "Company Address", key: "address" },
      { label: "Place", key: "place" },
    ],
  },
  {
    title: "Document Date",
    variables: [
      { label: "Current Date", key: "date" },
      { label: "Current Date (alias)", key: "currentDate" },
      { label: "Today", key: "today" },
    ],
  },
];

/**
 * What a promotion letter can say. On top of who the person is, it carries the
 * designation they are moving FROM and TO and the date the promotion takes
 * effect - none of which can be read off the employee record once the change
 * has been saved, so they are supplied by the promotion itself.
 */
const PROMOTION_VARIABLE_GROUPS = [
  {
    title: "Employee",
    variables: [
      { label: "Name", key: "name" },
      { label: "Employee Full Name", key: "employee_full_name" },
      { label: "First Name", key: "employee_first_name" },
      { label: "Last Name", key: "lastName" },
      { label: "Initial (Mr./Ms.)", key: "initialName" },
      { label: "Employee Code", key: "employee_code" },
      { label: "Employee Code (alias)", key: "emp_code" },
      { label: "Employee Address", key: "employee_address" },
      { label: "Name (camelCase alias)", key: "employeeName" },
      { label: "First Name (camelCase alias)", key: "employeeFirstName" },
      { label: "Employee Code (camelCase alias)", key: "employeeCode" },
    ],
  },
  {
    title: "Promotion",
    variables: [
      { label: "New Designation", key: "new_designation_name" },
      { label: "Previous Designation", key: "previous_designation_name" },
      { label: "Effective Date (w.e.f.)", key: "promotion_effective_date" },
      { label: "Current Designation on record", key: "designation_name" },
      { label: "New Designation (camelCase alias)", key: "newDesignationName" },
      { label: "Previous Designation (camelCase alias)", key: "previousDesignationName" },
      { label: "Effective Date (camelCase alias)", key: "promotionEffectiveDate" },
      { label: "Designation (camelCase alias)", key: "designation" },
    ],
  },
  {
    title: "Role & Tenure",
    variables: [
      { label: "Department", key: "department_name" },
      { label: "Work Location / Branch", key: "work_location" },
      { label: "Date of Joining", key: "date_of_joining" },
      { label: "Tenure", key: "tenure" },
      { label: "Reporting Manager", key: "reporting_manager_name" },
    ],
  },
  {
    title: "Company",
    variables: [
      { label: "Company Name", key: "companyName" },
      { label: "Company Name (alias)", key: "company_name" },
      { label: "Company Address", key: "address" },
      { label: "Place", key: "place" },
    ],
  },
  {
    title: "Letter Date",
    variables: [
      { label: "Current Date", key: "date" },
      { label: "Current Date (alias)", key: "currentDate" },
      { label: "Today", key: "today" },
    ],
  },
];

// An appraisal letter is raised for someone who already works here, so it draws
// on the live employee record - current designation, department and payroll
// figures - rather than anything captured at offer time. Amounts a letter
// announces (revised CTC, effective date) exist in no HRMS record, so a
// template can invent its own [{variable}] for them: the Generate Document form
// asks for anything it does not recognise.
const APPRAISAL_VARIABLE_GROUPS = [
  {
    title: "Employee",
    variables: [
      { label: "Name", key: "name" },
      { label: "Employee Full Name", key: "employee_full_name" },
      { label: "First Name", key: "firstName" },
      { label: "Last Name", key: "lastName" },
      { label: "Initial (Mr./Ms.)", key: "initialName" },
      { label: "Employee Code", key: "emp_code" },
      { label: "Email", key: "email" },
      { label: "Employee Address", key: "employee_address" },
    ],
  },
  {
    title: "Role & Placement",
    variables: [
      { label: "Designation", key: "designation_name" },
      { label: "Department", key: "department_name" },
      { label: "Work Location", key: "work_location" },
      { label: "Date of Joining", key: "date_of_joining" },
      { label: "Length of Service", key: "tenure" },
      { label: "Reporting Manager", key: "reporting_manager_name" },
      { label: "Reporting Manager Designation", key: "reporting_manager_designation" },
    ],
  },
  {
    title: "Current Compensation",
    variables: [
      { label: "Annual CTC", key: "ctc_salary" },
      { label: "Annual CTC (alias)", key: "current_ctc" },
      { label: "Monthly Gross", key: "gross_salary" },
      { label: "Basic Salary", key: "basic_salary" },
      { label: "Salary Grade", key: "salary_grade" },
      { label: "Appraisal Month", key: "appraisal_month" },
      { label: "Confirmation Date", key: "confirmation_date" },
    ],
  },
  {
    title: "Company",
    variables: [
      { label: "Company Name", key: "companyName" },
      { label: "Company Name (alias)", key: "company_name" },
      { label: "Company Address", key: "address" },
      { label: "Place", key: "place" },
    ],
  },
  {
    title: "Letter Date",
    variables: [
      { label: "Current Date", key: "date" },
      { label: "Current Date (alias)", key: "currentDate" },
      { label: "Today", key: "today" },
    ],
  },
];

// The signatory block is all a separation letter needs from Company Document
// Master - no photos, emails or onboarding-only fields.
/**
 * What the employee-completed forms actually use.
 *
 * These are printed, filled in by hand and uploaded back, so the palette stays
 * deliberately short: everything here is a header field that saves the employee
 * from writing out what the system already knows. Offering bank details or
 * salary on a form that gets handed round an office would be a privacy problem,
 * not a convenience.
 */
const REVIEW_VARIABLE_GROUPS = [
  {
    title: "Employee",
    variables: [
      { label: "Name", key: "name" },
      { label: "Employee Code", key: "emp_code" },
      { label: "Designation", key: "designation_name" },
      { label: "Department", key: "department_name" },
      { label: "Date of Joining", key: "doj" },
      { label: "Work Location", key: "work_location" },
    ],
  },
  {
    title: "Review",
    variables: [
      { label: "Reporting Manager", key: "reporting_manager_name" },
      { label: "Mentor (buddy, else reporting manager)", key: "mentor_name" },
      { label: "Form Type", key: "review_type" },
      { label: "Due Date", key: "review_due_date" },
      { label: "Date Printed", key: "review_date" },
    ],
  },
];

/**
 * What the new-joiner pack documents draw on.
 *
 * These four are filled from the employee record itself rather than from a
 * separate workflow, so the palette is wide -- an appointment letter needs the
 * offer terms, the personal data form needs the whole identity block, and the
 * PF sheet needs the statutory numbers. Two pairs of look-alike tokens live in
 * here and are labelled apart deliberately: `address` and `pan_number` are the
 * COMPANY's (they come from Company Document Master and are used as a
 * letterhead), while `permanent_address` and `employee_pan` are the employee's.
 * Picking the wrong one of either pair produces a document that looks correct
 * and is wrong on every copy.
 */
const JOINING_DOCS_VARIABLE_GROUPS = [
  {
    title: "Identity",
    variables: [
      { label: "Name", key: "name" },
      { label: "Employee Code", key: "emp_code" },
      { label: "Employee Photo (image URL)", key: "employee_photo" },
      { label: "Gender", key: "gender" },
      { label: "Date of Birth", key: "birthdate" },
      { label: "Marital Status", key: "maritalStatus" },
      { label: "Father Name", key: "fatherName" },
      { label: "Mother Name", key: "motherName" },
      { label: "Spouse Name", key: "spouse_name" },
      { label: "Blood Group", key: "blood_group" },
      { label: "Nationality", key: "nationality" },
      { label: "Religion", key: "religion" },
      { label: "Qualifications", key: "qualifications" },
      { label: "Education Summary", key: "education_summary" },
    ],
  },
  {
    title: "Contact & Address",
    variables: [
      { label: "Official Email", key: "email" },
      { label: "Personal Email", key: "personal_email" },
      { label: "Contact Number", key: "contactNumber" },
      { label: "Personal Mobile", key: "personal_mobile" },
      { label: "Employee Permanent Address (the employee's own, NOT the company's)", key: "permanent_address" },
      { label: "Employee Present Address", key: "present_address" },
      { label: "Emergency Contact Name", key: "emergency_contact_name" },
      { label: "Emergency Contact Number", key: "emergency_contact_number" },
      { label: "Emergency Contact Relation", key: "emergency_contact_relation" },
    ],
  },
  {
    title: "Statutory & Bank",
    variables: [
      { label: "Employee PAN (the employee's own PAN)", key: "employee_pan" },
      { label: "Employee Aadhaar", key: "employee_aadhaar" },
      { label: "UAN Number", key: "uan_number" },
      { label: "PF Number", key: "pf_number" },
      { label: "ESIC Number", key: "esic_number" },
      { label: "Company PAN (the COMPANY's PAN, NOT the employee's)", key: "pan_number" },
      { label: "Bank Name", key: "bankName" },
      { label: "Account Holder Name", key: "accountHolderName" },
      { label: "Account Number", key: "accountNumber" },
      { label: "IFSC Code", key: "ifsc" },
      { label: "Bank Branch", key: "bankBranch" },
    ],
  },
  {
    title: "Job & Dates",
    variables: [
      { label: "Designation", key: "designation_name" },
      { label: "Department", key: "department_name" },
      { label: "Work Location", key: "work_location" },
      { label: "Date of Joining", key: "doj" },
      { label: "Reporting Manager", key: "reporting_manager_name" },
      { label: "Monthly Salary", key: "monthly_salary" },
      { label: "Annual CTC", key: "annual_ctc" },
      { label: "Basic Salary", key: "basic_salary" },
      { label: "Gross Salary", key: "gross_salary" },
      { label: "Current Date", key: "currentDate" },
    ],
  },
  {
    title: "Company",
    variables: [
      { label: "Company Name", key: "companyName" },
      { label: "Company Address (letterhead, NOT the employee's address)", key: "address" },
      { label: "HR Signature (image URL)", key: "hr_sign" },
    ],
  },
];

/**
 * The plain-language reference note for each pack document.
 *
 * These four are raised by a background job rather than by anyone pressing a
 * button, so "what is this document and where does it come from?" is not
 * answerable anywhere else on this screen.
 */
const JOINING_DOC_NOTES = {
  appointment_letter:
    "The formal letter of appointment a new joiner signs: role, start date and terms of employment.",
  id_card:
    "The printed employee ID card. Keep the layout small and fixed-width; it is a card, not a page.",
  personal_data_form:
    "The personal data sheet HR keeps on file: identity, addresses, statutory numbers, bank details and emergency contact.",
  pf_registration_sheet:
    "The PF / EPFO registration sheet handed to the PF consultant.",
};

const SEPARATION_COMPANY_DOC_MASTER_KEYS = [
  "hr_name", "hr_sign", "hr_signature",
  "director_name", "director_sign",
  "ceo_name", "ceo_sign",
  "stamp_duty", "stampDuty",
];

/**
 * Why this PF sheet template would export a blank spreadsheet, or "" if it is fine.
 *
 * The .xlsx is produced by reading the template's table: the first row becomes
 * the column headings and the next row supplies the values. A template that
 * stops after the headings therefore exports a file with nothing under any of
 * them - which is exactly what a hand-built 28-column <thead> with no <tbody>
 * row does, and it gives no hint that anything went wrong.
 */
const describePfSheetProblem = (html, variables) => {
  if (!html) return "";

  const tables = html.match(/<table\b/gi) || [];
  if (!tables.length) {
    return (
      "This document is exported to Excel, so it needs a table. Add one whose first " +
      'row is the column headings and second row is the values, or use "Start from ' +
      'built-in layout".'
    );
  }

  const rows = html.match(/<tr\b/gi) || [];
  if (rows.length < 2) {
    return (
      "This table has column headings but no data row, so the exported sheet would be " +
      "blank under every heading. Add one row of variables beneath the headings, or " +
      'use "Start from built-in layout", which includes it.'
    );
  }

  if (!variables || !variables.length) {
    return (
      "This template contains no variables, so nothing would be filled in. Insert the " +
      'variables you want in the data row, or use "Start from built-in layout".'
    );
  }

  return "";
};

const isSeparationDoc = (docFor) => EXIT_DOC_FORS.includes(docFor);
const isSignatoryOnlyDoc = (docFor) => SIGNATORY_ONLY_DOC_FORS.includes(docFor);

/** Variable groups recommended for one document type. */
const getVariableGroupsFor = (docFor) => {
  if (REVIEW_DOC_FORS.includes(docFor)) return REVIEW_VARIABLE_GROUPS;
  if (JOINING_DOC_FORS.includes(docFor)) return JOINING_DOCS_VARIABLE_GROUPS;
  if (docFor === DISCONTINUATION_DOC_FOR) return DISCONTINUATION_VARIABLE_GROUPS;
  if (docFor === PROMOTION_DOC_FOR) return PROMOTION_VARIABLE_GROUPS;
  if (docFor === APPRAISAL_DOC_FOR) return APPRAISAL_VARIABLE_GROUPS;
  if (SEPARATION_DOC_FORS.includes(docFor)) return SEPARATION_VARIABLE_GROUPS;
  return ONBOARDING_VARIABLE_GROUPS;
};

/** What the variable-help panel says it is listing. */
const getVariableScopeHint = (docFor) => {
  if (REVIEW_DOC_FORS.includes(docFor)) {
    return "Showing the header fields these forms pre-fill. The employee completes the rest by hand, so keep the body blank.";
  }
  if (JOINING_DOC_FORS.includes(docFor)) {
    return "Showing what the new-joiner pack documents fill in from the employee record. Other variables still work if you type them.";
  }
  if (docFor === PROMOTION_DOC_FOR) {
    return "Showing the variables a promotion letter normally needs. Other variables still work if you type them.";
  }
  if (isSeparationDoc(docFor)) {
    return "Showing the variables a separation letter normally needs. Other variables still work if you type them.";
  }
  if(docFor === APPRAISAL_DOC_FOR) {
    return "Showing the variables an appraisal letter normally needs. Any other variable you type is asked for when the letter is generated.";
  }
  return "Showing every variable available to onboarding documents.";
};

/** Company Document Master variables recommended for one document type. */
const getCompanyDocMasterVariablesFor = (docFor) =>
  isSignatoryOnlyDoc(docFor)
    ? COMPANY_DOC_MASTER_VARIABLES.filter((v) => SEPARATION_COMPANY_DOC_MASTER_KEYS.includes(v.key))
    : COMPANY_DOC_MASTER_VARIABLES;

const DocumentMaster = () => {
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const isAdmin = user_data?.role_id?.role_name == "Admin";
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [documentList, setDocumentList] = useState([]);
  const [editingRecord, setEditingRecord] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE });
  const [content, setContent] = useState("");
  const [documentName, setDocumentName] = useState("");
  const [dynamicVariables, setDynamicVariables] = useState([]);
  const searchRef = useRef();
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);
  const dispatch = useDispatch();
  const [editorInstance, setEditorInstance] = useState(null);
  const [showSourceCode, setShowSourceCode] = useState(false);
  /**
   * doc_for values the server ships a built-in layout for - the letters under
   * server/templetes/defaults/ and the employee-completed forms under
   * server/templetes/ReviewFormTemplates/. Fetched rather than hard-coded so
   * this list cannot drift from what the server can actually supply.
   */
  const [letterTypesWithDefault, setLetterTypesWithDefault] = useState([]);
  const [loadingDefaultLayout, setLoadingDefaultLayout] = useState(false);
  const [sourceCode, setSourceCode] = useState("");
  const debounceTimeoutRef = useRef(null);
  const [empTypeOptions, setEmpTypeOptions] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  // "Applies to N employees", recomputed as the admin changes the targeting.
  const [matchCount, setMatchCount] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [isVariableModalOpen, setIsVariableModalOpen] = useState(false);
  const [docFor, setDocFor] = useState("onboarding");

  // Add Fields for Document
  const [isFieldsModalOpen, setIsFieldsModalOpen] = useState(false);
  const [documentFieldsList, setDocumentFieldsList] = useState([]);
  const [newFieldKey, setNewFieldKey] = useState("");
  const [newFieldValueType, setNewFieldValueType] = useState("text");
  const [pendingNewFields, setPendingNewFields] = useState([]);

  // Helper function to detect if content is a full HTML document
  const isFullHtmlDocument = (html) => {
    if (!html) return false;
    const trimmed = html.trim();
    return trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html');
  };

  // 👇 Compute if any field has a value
  const hasAnyValue = formValues
    ? Object.values(formValues).some((v) => v !== undefined && v !== null && v !== "")
    : false;

  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
    setEditingRecord(null);
    setContent("");
    setDocumentName("");
    setDynamicVariables([]);
    setShowSourceCode(false);
    setSourceCode("");
    setDocFor("onboarding");
    setMatchCount(null);
  };

  /** Designation and department options for the targeting multi-selects. */
  const getTargetingOptions = useCallback(async () => {
    try {
      const [designRes, deptRes] = await Promise.all([
        Service.makeAPICall({ methodName: Service.postMethod, api_url: Service.empDesignationList }),
        Service.makeAPICall({ methodName: Service.postMethod, api_url: Service.empDepartmentList }),
      ]);
      if (designRes?.data?.status === 200) {
        setDesignationOptions(
          (designRes.data.data || []).map((item) => ({
            label: item.designation_name,
            value: item._id,
          }))
        );
      }
      if (deptRes?.data?.status === 200) {
        setDepartmentOptions(
          (deptRes.data.data || []).map((item) => ({
            label: item.department_name,
            value: item._id,
          }))
        );
      }
    } catch (error) {
      // Non-fatal: the selects simply stay empty, which still means "applies to
      // all". Blocking the whole form because a dropdown failed would be worse.
      console.log("Error fetching targeting options:", error);
    }
  }, []);

  const getEmpTypeOptions = useCallback(async () => {
    try {
      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: Service.listEmploymentTypes,
        body: {},
      });

      if (response.data && response.data.status === 200 && response.data.data) {
        const transformed = response.data.data.map(item => ({
          label: item.employment_type_name,
          value: item._id,
        }));
        setEmpTypeOptions(transformed);
      }
    } catch (error) {
      console.log("Error fetching employee types:", error);
      message.error("Error fetching employee types");
    }
  }, []);

  const extractDynamicVariables = useCallback((html) => {
    // matches [{name}] with optional spaces: [{ name }]
    const regex = /\[\{\s*([a-zA-Z0-9_]+)\s*\}\]/g;
    const vars = new Set();
    let match;
    while ((match = regex.exec(html || "")) !== null) {
      vars.add(match[1]);
    }
    return Array.from(vars);
  }, []);

  const sanitizeHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    div.querySelectorAll("script").forEach(el => el.remove());
    return div.innerHTML;
  };

  const addInlineTableStyles = (htmlContent) => {
    const div = document.createElement('div');
    div.innerHTML = htmlContent;

    div.querySelectorAll('table').forEach(table => {
      if (!table.style.border || table.style.border === '') {
        table.setAttribute('style', 'border-collapse: collapse; width: 100%; border: 1px solid black;');
      }

      table.querySelectorAll('th, td').forEach(cell => {
        if (!cell.style.border || cell.style.border === '') {
          const firstRow = table.querySelector('tr');
          const columns = firstRow ? firstRow.children.length : 1;
          const cellWidth = `${100 / columns}%`;
          cell.setAttribute(
            'style',
            `border: 1px solid black; padding: 8px; text-align: left; width: ${cellWidth};`
          );
        }
      });
    });

    return div.innerHTML;
  };

  const toggleSourceMode = () => {
    if (!showSourceCode) {
      // Switching FROM editor TO source mode
      // Get current content from editor if available
      let currentContent = content;
      if (editorInstance) {
        try {
          currentContent = editorInstance.getData();
        } catch (e) {
          console.log("Could not get editor data:", e);
        }
      }
      
      // IMPORTANT: Only update sourceCode if it's NOT a full HTML document
      // If sourceCode already contains full HTML, preserve it (don't overwrite with CKEditor's processed version)
      if (!isFullHtmlDocument(sourceCode)) {
        // Store raw content in sourceCode - don't modify it
        setSourceCode(currentContent);
      }
      // If sourceCode is full HTML, keep it as-is (don't overwrite with CKEditor's <p> wrapped version)
      
      setContent(currentContent);
      setShowSourceCode(true);
    } else {
      // Switching FROM source TO editor mode
      // Check if it's a full HTML document - if so, prevent switching
      if (isFullHtmlDocument(sourceCode)) {
        message.warning('Full HTML documents (with <!DOCTYPE> or <html>) must be edited in source code mode only. Please continue editing in source mode.');
        return; // Don't switch to editor mode - stay in source mode
      }
      
      // Only allow switching to editor if it's NOT a full HTML document
      // When switching to editor, update content from source code
      setContent(sourceCode);
      setShowSourceCode(false);
      // Force editor to reload with new content
      if (editorInstance) {
        try {
          editorInstance.setData(sourceCode);
        } catch (e) {
          console.log("Could not set editor data:", e);
        }
      }
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const response = await Service.makeAPICall({
          methodName: Service.getMethod,
          api_url: Service.defaultLetterTypes,
        });
        if (response?.data?.status === 200 && Array.isArray(response?.data?.data)) {
          setLetterTypesWithDefault(response.data.data);
        }
      } catch (error) {
        // Just means the "start from the built-in layout" button stays hidden.
        setLetterTypesWithDefault([]);
      }
    })();
  }, []);

  /**
   * Drop the built-in layout into the editor as a starting point.
   *
   * Always lands in source mode: these are complete `<!DOCTYPE html>` documents,
   * and CKEditor's visual mode would rewrite the structure the letter depends on.
   */
  const handleLoadDefaultLayout = async () => {
    const hasContent = !!(sourceCode || content || "").replace(/<[^>]*>/g, "").trim();
    if (hasContent) {
      const proceed = window.confirm(
        "This replaces the current document content with the built-in layout. Continue?"
      );
      if (!proceed) return;
    }

    setLoadingDefaultLayout(true);
    try {
      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: `${Service.defaultLetterHtml}?doc_for=${encodeURIComponent(docFor)}`,
      });

      const html = response?.data?.data?.html || "";
      if (response?.data?.status !== 200 || !html) {
        message.error(response?.data?.message || "No built-in layout is available for this document type");
        return;
      }

      setSourceCode(html);
      setContent(html);
      setDynamicVariables(extractDynamicVariables(html));
      setShowSourceCode(true);
      message.success("Built-in layout loaded. Edit it as you like, then Save.");
    } catch (error) {
      message.error("Could not load the built-in layout");
    } finally {
      setLoadingDefaultLayout(false);
    }
  };

  const handleSourceCodeChange = (e) => {
    const newSourceCode = e.target.value;
    // Store raw source code without any modifications
    setSourceCode(newSourceCode);
    setContent(newSourceCode);
    setDynamicVariables(extractDynamicVariables(newSourceCode));
    
    // If user pastes full HTML document in source mode, keep it in source mode
    if (isFullHtmlDocument(newSourceCode)) {
      setShowSourceCode(true);
    }
  };

  const openEditModal = (record) => {
    getDocumentFields();
    setEditingRecord(record);
    form.setFieldsValue({
      document_name: record?.name || "",
      // Read the arrays, falling back to the legacy single `emp_type` so a
      // template saved before the targeting change still shows its restriction
      // rather than appearing to apply to everyone.
      emp_types: record?.emp_types?.length
        ? record.emp_types.map(String)
        : record?.emp_type
        ? [String(record.emp_type)]
        : [],
      designations: (record?.designations || []).map(String),
      departments: (record?.departments || []).map(String),
    });
    setDocFor(record?.doc_for || "onboarding");

    const htmlContent = record?.html || "";
    // Set both content and sourceCode to the raw HTML from database
    // This ensures we preserve the exact HTML structure
    setContent(htmlContent);
    setSourceCode(htmlContent);
    setDocumentName(record?.name || "");
    setDynamicVariables(record?.variables || extractDynamicVariables(htmlContent));
    
    // If it's a full HTML document, automatically switch to source code mode
    // This prevents CKEditor from wrapping content in <p> tags
    if (isFullHtmlDocument(htmlContent)) {
      setShowSourceCode(true);
    } else {
      setShowSourceCode(false);
    }
    
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    setEditingRecord(null);
    form.resetFields();
    setContent("");
    setDocumentName("");
    setDynamicVariables([]);
    setShowSourceCode(false);
    setSourceCode("");
    setDocFor("onboarding");
    getDocumentFields();
    setIsModalOpen(true);
  };

  const resetFields = () => form.resetFields();

  useEffect(() => {
    getDocumentList();
    getEmpTypeOptions();
    getTargetingOptions();
  }, [pagination.current, pagination.pageSize, searchText, getEmpTypeOptions, getTargetingOptions]);

  /**
   * "Applies to N employees", debounced.
   *
   * Turns an abstract targeting rule into a number before anything is saved,
   * which is the cheapest way to catch a rule that selects nobody - or everyone.
   */
  useEffect(() => {
    if (!isModalOpen) return undefined;
    const timer = setTimeout(async () => {
      try {
        setMatchLoading(true);
        const response = await Service.makeAPICall({
          methodName: Service.postMethod,
          api_url: Service.reviewFormCountMatchingEmployees,
          body: {
            emp_types: formValues?.emp_types || [],
            designations: formValues?.designations || [],
            departments: formValues?.departments || [],
          },
        });
        setMatchCount(
          response?.data?.status === 200 ? response.data.data?.count ?? null : null
        );
      } catch (error) {
        setMatchCount(null);
      } finally {
        setMatchLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [
    isModalOpen,
    formValues?.emp_types,
    formValues?.designations,
    formValues?.departments,
  ]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = async (values) => {
    console.log("🚀 ~ handleSubmit ~ values:", values)
    
    try {
      // ALWAYS use sourceCode as the source of truth for submission
      // sourceCode contains the raw, unmodified HTML
      // content may have been processed by CKEditor (wrapped in <p> tags, etc.)
      let finalContent = sourceCode && sourceCode.trim() ? sourceCode : content;
      
      // If sourceCode is a full HTML document, always use it (never use CKEditor's processed version)
      if (isFullHtmlDocument(sourceCode)) {
        finalContent = sourceCode;
      } else if (isFullHtmlDocument(content)) {
        // If content is full HTML but sourceCode isn't, use content
        // This handles edge cases
        finalContent = content;
      }
      
      if (!finalContent || finalContent.trim() === '') {
        message.error('Document content cannot be empty');
        return;
      }

      const extractedVars = extractDynamicVariables(finalContent);
      const docFieldKeys = (documentFieldsList || []).map((f) => f.key);
      const companyDocMasterKeys = COMPANY_DOC_MASTER_VARIABLES.map((v) => v.key);
      const allowedKeys = [...new Set([...docFieldKeys, ...companyDocMasterKeys, ...ALLOWED_SYSTEM_VARIABLES])];
      if (extractedVars.length > 0) {
        const invalid = extractedVars.filter((v) => !allowedKeys.includes(v));
        if (invalid.length > 0) {
          // An appraisal letter is always raised by hand from an employee's
          // Documents tab, and that form asks for anything it cannot resolve -
          // so a letter may carry its own placeholders (revised CTC, effective
          // date) that live in no HRMS record. Every other document type is
          // generated unattended, where an unknown variable can only ever come
          // out blank, so it stays rejected.
          if (docFor !== APPRAISAL_DOC_FOR) {
            message.error(
              `Invalid variables: ${invalid.join(", ")}. Use only: Document Fields (Add Fields), Company Document Master, or system variables (View All Variables).`
            );
            return;
          }
          message.info(
            `${invalid.join(", ")} will be asked for each time this letter is generated.`
          );
        }
      }

      // The PF sheet is exported to Excel by reading its table, so a template
      // with column headings and no data row produces a spreadsheet that is
      // blank under every heading. That parses cleanly and downloads without
      // complaint, so it is caught here - at the point where it can still be
      // fixed - rather than being discovered by whoever opens the file.
      if (docFor === PF_REGISTRATION_DOC_FOR) {
        const problem = describePfSheetProblem(finalContent, extractedVars);
        if (problem) {
          message.error(problem, 8);
          return;
        }
      }

      dispatch(showAuthLoader());

      const reqBody = {
        name: values.document_name || documentName,
        html: finalContent, // Send raw HTML exactly as entered/pasted - no modifications
        variables: extractedVars,
        // Targeting. An empty array means "applies to all"; the server keeps
        // the legacy scalar `emp_type` in step for one release.
        emp_types: values.emp_types || [],
        designations: values.designations || [],
        departments: values.departments || [],
        doc_for: docFor,
      };

      let response;
      if (editingRecord) {
        // Update existing document
        response = await Service.makeAPICall({
          methodName: Service.postMethod,
          api_url: `${Service.editDocumentTemplate}/${editingRecord._id}`,
          body: reqBody,
        });
      } else {
        // Create new document
        response = await Service.makeAPICall({
          methodName: Service.postMethod,
          api_url: Service.addDocumentTemplate,
          body: reqBody,
        });
      }

      if (response.data.status === 200) {
        message.success(response.data.message || `Document ${editingRecord ? 'updated' : 'created'} successfully`);
        form.resetFields();
        getDocumentList();
        handleCancel();
      } else {
        message.error(response.data.message || `Error ${editingRecord ? 'updating' : 'creating'} document`);
      }
      dispatch(hideAuthLoader());
    } catch (error) {
      dispatch(hideAuthLoader());
      console.log("Document Error:", error);
      message.error(`Error ${editingRecord ? 'updating' : 'creating'} document`);
    }
  };

  const handleDelete = async (record) => {
    try {
      dispatch(showAuthLoader());

      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: `${Service.deleteDocumentTemplate}/${record._id}`,
        body: {},
      });

      if (response.data.status === 200) {
        message.success(response.data.message || "Document deleted successfully");
        getDocumentList();
      } else {
        message.error(response.data.message || "Error deleting document");
      }
      dispatch(hideAuthLoader());
    } catch (error) {
      dispatch(hideAuthLoader());
      console.log("Delete Document Error:", error);
      message.error("Error deleting document");
    }
  };

  const columns = [
    {
      title: "Document Name",
      dataIndex: "name",
      key: "name",
      render: (name) => <span>{capitalizeFirst(name) || "N/A"}</span>,
    },
    {
      title: "Document For",
      dataIndex: "doc_for",
      key: "doc_for",
      render: (docForValue) => (
        <Tag color="blue">{DOC_FOR_LABELS[docForValue] || DOC_FOR_LABELS.onboarding}</Tag>
      ),
    },
    {
      title: "Dynamic Variables",
      dataIndex: "variables",
      key: "variables",
      render: (variables) => (variables?.length ? variables.join(", ") : "-"),
    },
    {
      title: "Actions",
      key: "action",
      render: (_, record) => (
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          <Tooltip title="Edit Document" placement="bottom">
            <Button type="link" onClick={() => openEditModal(record)}>
              <EditIcon />
            </Button>
          </Tooltip>
          <Tooltip title="Delete Document" placement="bottom">
            <Popconfirm
              title="Delete Document"
              description={`Are you sure you want to delete ${record?.name}?`}
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button icon={<DeleteIcon />} />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    }
  ];

  const getDocumentFields = useCallback(async () => {
    try {
      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: Service.listDocumentFields,
        body: {},
      });
      if (response.data?.status === 200 && response.data?.data) {
        setDocumentFieldsList(response.data.data);
      }
    } catch (error) {
      console.log("Error fetching document fields:", error);
      message.error("Error fetching document fields");
    }
  }, []);

  const openFieldsModal = () => {
    setPendingNewFields([]);
    setNewFieldKey("");
    setNewFieldValueType("text");
    getDocumentFields();
    setIsFieldsModalOpen(true);
  };

  const closeFieldsModal = () => {
    setIsFieldsModalOpen(false);
    setPendingNewFields([]);
    setNewFieldKey("");
  };

  const addPendingField = () => {
    const key = (newFieldKey || "").trim().replace(/\s+/g, "_");
    if (!key) {
      message.warning("Please enter a field key.");
      return;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(key)) {
      message.warning("Key can only contain letters, numbers and underscores.");
      return;
    }
    const exists = documentFieldsList.some((f) => f.key === key) || pendingNewFields.some((f) => f.key === key);
    if (exists) {
      message.warning(`Field key "${key}" already exists.`);
      return;
    }
    setPendingNewFields((prev) => [...prev, { key, valueType: newFieldValueType }]);
    setNewFieldKey("");
    setNewFieldValueType("text");
  };

  const removePendingField = (index) => {
    setPendingNewFields((prev) => prev.filter((_, i) => i !== index));
  };

  const saveNewFields = async () => {
    if (pendingNewFields.length === 0) {
      message.info("No new fields to save.");
      return;
    }
    try {
      dispatch(showAuthLoader());
      for (const field of pendingNewFields) {
        await Service.makeAPICall({
          methodName: Service.postMethod,
          api_url: Service.addDocumentField,
          body: { key: field.key, valueType: field.valueType },
        });
      }
      message.success(`${pendingNewFields.length} field(s) added successfully.`);
      setPendingNewFields([]);
      getDocumentFields();
      dispatch(hideAuthLoader());
    } catch (error) {
      dispatch(hideAuthLoader());
      message.error("Error saving fields.");
    }
  };

  const deleteDocumentFieldById = async (id) => {
    try {
      dispatch(showAuthLoader());
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: `${Service.deleteDocumentField}/${id}`,
        body: {},
      });
      if (response.data?.status === 200) {
        message.success("Field removed.");
        getDocumentFields();
      } else {
        message.error(response.data?.message || "Error removing field.");
      }
      dispatch(hideAuthLoader());
    } catch (error) {
      dispatch(hideAuthLoader());
      message.error("Error removing field.");
    }
  };

  const getDocumentList = useCallback(async () => {
    try {
      dispatch(showAuthLoader());
      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: Service.listDocumentTemplate,
        body: {},
      });

      let documentData = response.data?.data || [];
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        documentData = documentData.filter((item) => {
          const documentName = item?.name?.toLowerCase() || "";
          const vars = (item?.variables || []).join(" ").toLowerCase();
          return (
            documentName.includes(searchLower) ||
            vars.includes(searchLower)
          );
        });
      }

      const startIndex = (pagination.current - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize;

      setDocumentList(documentData.slice(startIndex, endIndex));
      setPagination({ ...pagination, total: documentData.length });
      dispatch(hideAuthLoader());
    } catch (error) {
      dispatch(hideAuthLoader());
      message.error("Error fetching document list");
      setDocumentList([]);
    }
  }, [searchText, pagination.current, pagination.pageSize]);

  const handleTableChange = (page) => setPagination({ ...pagination, ...page });

  const handlePreviewClick = () => {
    const styledContent = addInlineTableStyles(sanitizeHtml(content));

    const newWindow = window.open('', '_blank');
    newWindow.document.write(`
      <html>
        <head>
          <title>Document Preview</title>
          <style id="pdf-ligature-fix">
            * {
              font-feature-settings: "liga" 0, "clig" 0, "dlig" 0 !important;
              -webkit-font-feature-settings: "liga" 0, "clig" 0, "dlig" 0 !important;
              font-variant-ligatures: none !important;
            }
          </style>
        </head>
        <body style="margin:0;padding:20px;font-family:Arial,sans-serif;">
          ${styledContent}
        </body>
      </html>
    `);
    newWindow.document.close();
  };

  const editorConfiguration = {
    toolbar: {
      items: [
        'undo', 'redo',
        '|',
        'heading',
        '|',
        'bold', 'italic', 'underline', 'strikethrough', 'subscript', 'superscript',
        '|',
        'blockQuote', 'code', 'codeBlock',
        '|',
        'alignment',
        '|',
        'bulletedList', 'numberedList', 'todoList', 'outdent', 'indent',
        '|',
        'insertTable',
        '|',
        'specialCharacters', 'horizontalLine',
        '|',
        'removeFormat'
      ],
      shouldNotGroupWhenFull: true
    },
    // Make CKEditor preserve ALL HTML content without modification
    htmlSupport: {
      allow: [
        {
          name: /.*/,
          attributes: true,
          classes: true,
          styles: true
        }
      ],
      disallow: [
        {
          name: 'script',
        },
        { attributes: [/^on.*/] },
      ]
    },
    // Disable data filtering to preserve all HTML
    removePlugins: [],
    // Preserve all HTML structure
    preserveWhitespace: true,
    // Don't filter data
    dataFilter: null,
    table: {
      tableProperties: {
        defaultProperties: {
          width: 'auto',
          alignment: 'left',
          borderStyle: 'solid',
          borderColor: '#d9d9d9',
          borderWidth: '1px'
        }
      },
      tableCellProperties: {
        defaultProperties: {
          horizontalAlignment: 'left',
          verticalAlignment: 'middle',
          padding: '4px 8px',
          width: 'auto',
          height: 'auto'
        }
      }
    }
  };

  return (
    <>
      <Card>
        <div className="inout-header text-left" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <h2>{isAdmin ? "Document Master" : "Document Templates"}</h2>
          {isAdmin && (
            <>
              <Button type="default" onClick={openFieldsModal}>
                Add Fields for Document
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                Document
              </Button>
            </>
          )}
        </div>
        <Card className="main-content-wrapper">
          <div className="search-wrapper">
            <Input.Search
              ref={searchRef}
              placeholder="Search..."
              onSearch={(val) => setSearchText(val)}
              style={{ width: 200, marginBottom: 16 }}
            />
          </div>
          {documentList.length === 0 && !searchText && (
            <div style={{ 
              marginBottom: 16, 
              padding: '16px', 
              backgroundColor: '#e6f7ff', 
              border: '1px solid #91d5ff', 
              borderRadius: '4px',
              fontSize: '14px',
              color: '#0050b3'
            }}>
              <strong>📋 No document templates configured yet.</strong> Click the <strong>"Add Document"</strong> button above to create your first document template. These templates will be used during employee onboarding and can be generated from the employee documents section.
            </div>
          )}
          <div className="main-table-wrapper">
            <Table
              columns={columns}
              dataSource={documentList}
              locale={{ emptyText: <NodataFoundIcon /> }}
              pagination={{ ...TABLE_PAGINATION, ...pagination }}
              rowKey="_id"
              onChange={handleTableChange}
            />
          </div>
        </Card>
      </Card>

      {/* Create/Edit Document Modal */}
      <Modal
        title={`${editingRecord ? 'Edit' : 'Create'} Document`}
        width={900}
        className="edit-content-modal"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={
          [
            <Button key="reset" onClick={resetFields} className="ant-delete" disabled={!hasAnyValue}>
              Reset
            </Button>,
            <Button key="submit" type="primary" onClick={() => form.submit()}>
              {editingRecord ? 'Update' : 'Create'}
            </Button>,
          ]
        }
      >
        <Form layout="vertical" className="modal-form-col" form={form} onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Document Name"
                name="document_name"
                rules={[{ required: true, message: "Please enter document name!" }]}
              >
                <Input
                  value={documentName}
                  onChange={(e) => setDocumentName(e.target.value)}
                  placeholder="Enter document name"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Document For">
                <Select
                  value={docFor}
                  onChange={(val) => setDocFor(val)}
                  style={{ width: "100%" }}
                >
                  {Object.entries(DOC_FOR_LABELS).map(([value, label]) => (
                    <Select.Option key={value} value={value}>{label}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            {TARGETING_FIELDS.map((field) => (
              <Col xs={24} md={8} key={field.name}>
                <Form.Item
                  label={field.label}
                  name={field.name}
                  tooltip={`Which ${field.label.toLowerCase()}s this document applies to. Leave empty to apply to all of them.`}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder={field.placeholder}
                    options={
                      field.name === "emp_types"
                        ? empTypeOptions
                        : field.name === "designations"
                        ? designationOptions
                        : departmentOptions
                    }
                    showSearch
                    maxTagCount="responsive"
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
            ))}
          </Row>
          <Row gutter={16}>
            <Col xs={24}>
              {/*
                The targeting rule is three ANDed dimensions, each of which
                means "all" when empty - easy to state, easy to get wrong. This
                line reports what the current selection actually resolves to.
              */}
              <div style={{ marginBottom: 16, color: "#666", fontSize: 13 }}>
                {matchLoading ? (
                  <span>Checking how many employees this applies to…</span>
                ) : matchCount === null ? (
                  <span>Leave a field empty to apply to all of that type.</span>
                ) : matchCount === 0 ? (
                  <span style={{ color: "#cf1322" }}>
                    ⚠ This targeting matches <strong>no active employees</strong>. Nobody will
                    receive this document.
                  </span>
                ) : (
                  <span>
                    Applies to <strong>{matchCount}</strong> active employee
                    {matchCount === 1 ? "" : "s"}.
                  </span>
                )}
              </div>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item label="Dynamic Variables (auto-detected)">
                <div style={{ minHeight: 32, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                  {dynamicVariables.length ? (
                    dynamicVariables.map((v) => <Tag key={v}>{`[{${v}}]`}</Tag>)
                  ) : (
                    <span style={{ color: "#999" }}>No variables found</span>
                  )}
                </div>
              </Form.Item>
            </Col>
          </Row>

          {JOINING_DOC_FORS.includes(docFor) && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${DOC_FOR_LABELS[docFor]}: part of the new joiner document pack`}
              description={
                <div style={{ fontSize: '12px' }}>
                  <div style={{ marginBottom: 6 }}>{JOINING_DOC_NOTES[docFor]}</div>
                  <div style={{ marginBottom: docFor === PF_REGISTRATION_DOC_FOR ? 6 : 0 }}>
                    It is raised <strong>automatically when an employee is created</strong>. Nobody
                    generates it by hand. An employee gets it only if a template here matches their
                    employment type, designation and department, so with no matching template the
                    document is simply never raised.
                  </div>
                  {docFor === PF_REGISTRATION_DOC_FOR && (
                    <div>
                      <strong>This one is exported to Excel.</strong> The template must contain a
                      single flat <code>&lt;table&gt;</code> whose <strong>first row is the header
                      row</strong>. Nested tables, merged cells and anything outside that table are
                      dropped from the sheet.
                    </div>
                  )}
                </div>
              }
            />
          )}

                    <div style={{
            backgroundColor: '#e6f7ff',
            border: '1px solid #91d5ff',
            borderRadius: '4px',
            padding: '12px 16px',
            marginBottom: '16px',
            fontSize: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Dynamic variable format:</strong> <code>[{'{name}'}]</code>, <code>[{'{email}'}]</code>, etc.
              </div>
              <div>
                Use variables in your template like <code>[{'{name}'}]</code> to replace with actual values.
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              {letterTypesWithDefault.includes(docFor) && (
                <Button
                  type="link"
                  onClick={handleLoadDefaultLayout}
                  loading={loadingDefaultLayout}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Start from built-in layout
                </Button>
              )}
              <Button
                type="link"
                icon={<InfoCircleOutlined />}
                onClick={() => setIsVariableModalOpen(true)}
                style={{ whiteSpace: 'nowrap' }}
              >
                View All Variables
              </Button>
            </div>
          </div>

          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {showSourceCode && (
              <span style={{ fontSize: '15px', color: '#666' }}>
                <strong>Editing HTML Source: Click Switch To Editor Button to return to visual editor</strong>
              </span>
            )}
          </div>
          

          <Form.Item required className="ck-main-editor" label="Document Content">
            {showSourceCode ? (
              <TextArea
                value={sourceCode}
                onChange={handleSourceCodeChange}
                rows={20}
                style={{
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  backgroundColor: '#f5f5f5',
                }}
                placeholder="Edit HTML source code here... You can use dynamic variables like [{name}], [{email}], etc."
              />
            ) : (
              <div style={{ borderRadius: '2px' }}>
                <CKEditor
                  editor={ClassicEditor}
                  data={content}
                  onReady={(editor) => {
                    setEditorInstance(editor);
                  }}
                  onChange={(event, editor) => {
                    const newContent = editor.getData();
                    setContent(newContent);
                    
                    // CRITICAL: Only update sourceCode if it's NOT a full HTML document
                    // If sourceCode contains full HTML, preserve it - don't overwrite with CKEditor's processed version
                    if (!isFullHtmlDocument(sourceCode)) {
                      // For non-full-HTML content, sync sourceCode with CKEditor changes
                      setSourceCode(newContent);
                    }
                    // If sourceCode is full HTML, keep it unchanged (CKEditor would have modified it)
                    
                    setDynamicVariables(extractDynamicVariables(newContent));
                    
                    // If user pastes full HTML document in CKEditor, automatically switch to source mode
                    // This prevents CKEditor from wrapping content in <p> tags
                    if (isFullHtmlDocument(newContent)) {
                      // Small delay to avoid interrupting the onChange event
                      setTimeout(() => {
                        setShowSourceCode(true);
                        // Don't update sourceCode here - it might already contain the correct full HTML
                        // The user should edit in source mode
                      }, 100);
                    }
                  }}
                  config={editorConfiguration}
                />
              </div>
            )}
          </Form.Item>

          <Row style={{ flexDirection: "row", align: "middle", gap: "10px", flexWrap: "wrap" }}>
            <Col>
              <Button onClick={handlePreviewClick} icon={<EyeOutlined />}>
                Preview
              </Button>
            </Col>
            <Col>
              <Button
                icon={<CodeOutlined />}
                onClick={toggleSourceMode}
                type={showSourceCode ? 'primary' : 'default'}
                disabled={isFullHtmlDocument(sourceCode) && !showSourceCode}
                title={isFullHtmlDocument(sourceCode) && !showSourceCode ? 'Full HTML documents must be edited in source mode' : ''}
              >
                {showSourceCode ? 'Switch to Editor' : 'View HTML Source'}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Variable Helper Modal */}
      <Modal
        title="Available Dynamic Variables"
        open={isVariableModalOpen}
        onCancel={() => setIsVariableModalOpen(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setIsVariableModalOpen(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
            <div style={{ marginBottom: '8px' }}>
              <Tag color="blue">{DOC_FOR_LABELS[docFor]}</Tag>
              <span style={{ color: '#666' }}>
                {getVariableScopeHint(docFor)}
                {" "}Change &quot;Document For&quot; to see another type&apos;s list.
              </span>
            </div>
            <strong>Usage:</strong> Use variables in your template like <code>[{'{name}'}]</code> to replace with actual values.
            <div style={{ marginTop: '8px', color: '#0050b3' }}>
              You can also use <strong>Company Document Master</strong> variables (CEO/HR/Director names, signatures, photos), listed below.
            </div>
          </div>

          {documentFieldsList?.length > 0 && docFor === "onboarding" && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ marginBottom: '12px', color: '#1890ff' }}>Your document fields (Add Fields for Document)</h3>
              <p style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                These are captured during onboarding, so they only fill in on onboarding documents.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {documentFieldsList.map((f) => (
                  <div key={f._id}><strong>{f.key}</strong> ({f.valueType}): <code>[{'{'}{f.key}{'}'}]</code></div>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '12px', color: '#1890ff' }}>
              {isSignatoryOnlyDoc(docFor)
                ? "Company Document Master (signatory block)"
                : "Company Document Master (CEO, Director, HR, signatures, media)"}
            </h3>
            <p style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
              These come from Company Document Master. Use in content as <code>[{'{variableName}'}]</code>. For signatures/photos use in HTML: <code>&lt;img src=&quot;[{'{hr_sign}'}]&quot; /&gt;</code>
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {getCompanyDocMasterVariablesFor(docFor).map((v) => (
                <div key={v.key}><strong>{v.label}</strong>: <code>[{'{'}{v.key}{'}'}]</code></div>
              ))}
            </div>
          </div>

          {getVariableGroupsFor(docFor).map((group) => (
            <div key={group.title} style={{ marginBottom: '20px' }}>
              <h3 style={{ marginBottom: '12px', color: '#1890ff' }}>{group.title}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {group.variables.map((v) => (
                  <div key={v.key}><strong>{v.label}:</strong> <code>[{'{'}{v.key}{'}'}]</code></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Modal>

      {/* Add Fields for Document Modal */}
      <Modal
        title="Add Fields for Document"
        open={isFieldsModalOpen}
        onCancel={closeFieldsModal}
        footer={[
          <Button key="close" onClick={closeFieldsModal}>Close</Button>,
          <Button key="save" type="primary" onClick={saveNewFields} disabled={pendingNewFields.length === 0}>
            Save New Fields
          </Button>,
        ]}
        width={640}
      >
        <p style={{ marginBottom: 16, color: "#666" }}>
          These fields define the variables you can use in document templates. Use only these variable names in &quot;Add Document&quot; content as <code>[{'{key}'}]</code>.
        </p>
        <Table
          dataSource={documentFieldsList}
          rowKey="_id"
          size="small"
          pagination={false}
          columns={[
            { title: "Key (variable name)", dataIndex: "key", render: (k) => <code>[{'{'}{k}{'}'}]</code> },
            { title: "Value type", dataIndex: "valueType", render: (t) => t === "number" ? "Number" : "Text" },
            {
              title: "Action",
              key: "action",
              render: (_, record) => (
                <Button type="link" danger size="small" onClick={() => deleteDocumentFieldById(record._id)}>
                  Remove
                </Button>
              ),
            },
          ]}
          locale={{ emptyText: "No fields defined yet. Add keys below." }}
        />
        <div style={{ marginTop: 16, padding: 12, background: "#fafafa", borderRadius: 4 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
            <Input
              placeholder="Field key (e.g. employee_id)"
              value={newFieldKey}
              onChange={(e) => setNewFieldKey(e.target.value)}
              onPressEnter={addPendingField}
              style={{ width: 200 }}
            />
            <Select
              value={newFieldValueType}
              onChange={setNewFieldValueType}
              options={VALUE_TYPES}
              style={{ width: 120 }}
            />
            <Button type="primary" onClick={addPendingField}>Add field</Button>
          </div>
          {pendingNewFields.length > 0 && (
            <div style={{ marginTop: 8 }}>
              New fields to save: {pendingNewFields.map((f, i) => (
                <Tag key={i} closable onClose={() => removePendingField(i)} style={{ marginTop: 4 }}>
                  {f.key} ({f.valueType})
                </Tag>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default DocumentMaster;
